import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { CalendarEventModel } from '../../../../../types';
import { deleteResourceEvent, UserKeys } from '../../../../api/users-api';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import SharedDialog from '../../../../shared/shared-dialog';
import { handleErrors } from '../../../../utils/errors';
import { useStore } from '../../../state/zustand-store';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface ResourceCalendarEventsDeleteDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedEvent: CalendarEventModel;
  changeLogRoles: string[];
  calendarId: string;
  dateRange: {
    start: number;
    end: number;
  };
}

export default function ResourceCalendarEventsDeleteDialog({
  open,
  handleClose,
  userEmail,
  calendarId,
  selectedEvent,
  changeLogRoles,
  dateRange,
}: ResourceCalendarEventsDeleteDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteResourceEvent(
        userEmail,
        calendarId,
        {
          id: selectedEvent.id,
          summary: selectedEvent.summary,
        },
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Calendar event deleted successfully!',
      });

      queryClient.setQueriesData(
        {
          queryKey: [
            UserKeys.CalendarEvents,
            calendarId,
            userEmail,
            {
              start: dateRange.start,
              end: dateRange.end,
            },
          ],
        },
        (oldData: CalendarEventModel[] | undefined) => {
          if (oldData) {
            return oldData.filter((event) => event.id !== selectedEvent.id);
          } else {
            return [];
          }
        }
      );
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting this calendar event. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.calendars.resource_calendars.delete_resource_calendar_event,
        {
          module: ModuleList.modules.calendars.name,
          section: ModuleList.modules.calendars.sections.resourceCalendars,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        CalendarRoles.RemoveResourceEvents.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Delete Calendar Event"
      dialogContent={
        <Box>
          <Typography>
            Delete calendar event: <strong>{selectedEvent?.summary}</strong>?
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete Event',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
